import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Sparkles, Upload, Link2, FileText, Loader2, Save, Wand2, Trash2 } from "lucide-react";

import { api } from "@/api/client";
import { useLang } from "@/context/LangContext";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const MODELS = [
  { provider: "gemini",    model: "gemini-3-flash-preview", label: "Gemini 3 Flash (rápido)" },
  { provider: "anthropic", model: "claude-sonnet-5",        label: "Claude Sonnet 5 (preciso)" },
  { provider: "openai",    model: "gpt-5.4-mini",           label: "GPT-5.4 Mini" },
];

const CATEGORIES = [
  { id: "sales", label: "Ventas" },
  { id: "service", label: "Servicio" },
  { id: "financing", label: "Financiamiento" },
  { id: "custom", label: "Personalizado" },
];

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const s = String(r.result || "");
      resolve(s.split(",")[1] || s);
    };
    r.onerror = reject;
    r.readAsDataURL(file);
  });

export default function AIGenerator() {
  const { lang } = useLang();

  const [source, setSource] = useState("url"); // url | text | pdf
  const [url, setUrl] = useState("");
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);

  const [modelIdx, setModelIdx] = useState(0);
  const [numQ, setNumQ] = useState(8);
  const [category, setCategory] = useState("custom");
  const [topic, setTopic] = useState("General");

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [meta, setMeta] = useState(null);

  const [createExam, setCreateExam] = useState(true);
  const [examTitleEs, setExamTitleEs] = useState("");
  const [examTitleEn, setExamTitleEn] = useState("");
  const [passingScore, setPassingScore] = useState(70);
  const [timeLimit, setTimeLimit] = useState(600);

  const generate = async () => {
    if (source === "url" && !url.trim()) return toast.error(lang === "es" ? "Ingresa una URL" : "Enter a URL");
    if (source === "text" && !text.trim()) return toast.error(lang === "es" ? "Pega el texto" : "Paste text");
    if (source === "pdf" && !file) return toast.error(lang === "es" ? "Selecciona un PDF" : "Select a PDF");

    setLoading(true);
    try {
      let body;
      if (source === "url") body = { source_type: "url", content: url.trim() };
      else if (source === "text") body = { source_type: "text", content: text };
      else {
        const b64 = await fileToBase64(file);
        body = { source_type: "pdf_base64", content: b64 };
      }
      const m = MODELS[modelIdx];
      body = { ...body, provider: m.provider, model: m.model, num_questions: numQ, category, topic };
      const { data } = await api.post("/admin/questions/generate", body);
      setQuestions(data.questions || []);
      setMeta({ count: data.count, source_chars: data.source_chars });
      toast.success(
        lang === "es"
          ? `${data.count} preguntas generadas`
          : `${data.count} questions generated`
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
    }
  };

  const patch = (i, key, val) => {
    setQuestions((qs) => qs.map((q, idx) => (idx === i ? { ...q, [key]: val } : q)));
  };

  const patchOpt = (i, oi, key, val) => {
    setQuestions((qs) =>
      qs.map((q, idx) =>
        idx === i
          ? { ...q, options: q.options.map((o, j) => (j === oi ? { ...o, [key]: val } : o)) }
          : q
      )
    );
  };

  const removeQ = (i) => setQuestions((qs) => qs.filter((_, idx) => idx !== i));

  const save = async () => {
    if (!questions.length) return;
    setSaving(true);
    try {
      const { data } = await api.post("/admin/questions/bulk", {
        questions,
        create_exam: createExam,
        exam_title_es: examTitleEs || (lang === "es" ? "Examen AI" : "AI Exam"),
        exam_title_en: examTitleEn || examTitleEs || "AI Exam",
        exam_category: category,
        time_limit_seconds: timeLimit ? Number(timeLimit) : null,
        passing_score: Number(passingScore),
      });
      toast.success(
        lang === "es"
          ? `${data.saved} guardadas${data.exam ? " · examen creado" : ""}`
          : `${data.saved} saved${data.exam ? " · exam created" : ""}`
      );
      if (data.exam?.slug) {
        setTimeout(() => (window.location.href = "/admin/exams"), 900);
      } else {
        setQuestions([]);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display font-black text-4xl flex items-center gap-2">
            <Wand2 className="w-8 h-8 text-canales-red" />
            {lang === "es" ? "Generador AI de Preguntas" : "AI Question Generator"}
          </h1>
          <p className="font-body text-neutral-600 mt-1">
            {lang === "es"
              ? "Sube un PDF, pega una URL o texto. La IA crea preguntas bilingües en 30 segundos."
              : "Upload a PDF, paste a URL or text. AI creates bilingual questions in 30 seconds."}
          </p>
        </div>
      </div>

      <div className="brutal p-6 bg-white space-y-4">
        {/* Source picker */}
        <div className="flex flex-wrap gap-2">
          {[
            { id: "url",  label: lang === "es" ? "URL" : "URL",  Icon: Link2 },
            { id: "pdf",  label: "PDF", Icon: Upload },
            { id: "text", label: lang === "es" ? "Texto" : "Text", Icon: FileText },
          ].map(({ id, label, Icon }) => (
            <button
              key={id}
              data-testid={`ai-src-${id}`}
              onClick={() => setSource(id)}
              className={`brutal-btn px-3 py-2 text-sm flex items-center gap-2 ${
                source === id ? "bg-canales-blue text-white" : "bg-white"
              }`}
            >
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </div>

        {source === "url" && (
          <div>
            <Label>URL</Label>
            <Input
              data-testid="ai-url-input"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://…"
            />
          </div>
        )}
        {source === "text" && (
          <div>
            <Label>{lang === "es" ? "Pega el texto/manual" : "Paste text / manual"}</Label>
            <Textarea
              data-testid="ai-text-input"
              rows={8}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={lang === "es" ? "Contenido de la fuente…" : "Source content…"}
            />
          </div>
        )}
        {source === "pdf" && (
          <div>
            <Label>PDF</Label>
            <input
              data-testid="ai-pdf-input"
              type="file"
              accept="application/pdf"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="block mt-1 font-body"
            />
            {file && <div className="text-xs mt-1">{file.name}</div>}
          </div>
        )}

        {/* Config */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div className="lg:col-span-2">
            <Label>{lang === "es" ? "Modelo IA" : "AI Model"}</Label>
            <Select value={String(modelIdx)} onValueChange={(v) => setModelIdx(Number(v))}>
              <SelectTrigger data-testid="ai-model-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {MODELS.map((m, i) => (
                  <SelectItem key={m.model} value={String(i)}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>{lang === "es" ? "# Preguntas" : "# Questions"}</Label>
            <Input
              data-testid="ai-numq-input"
              type="number"
              min={3}
              max={30}
              value={numQ}
              onChange={(e) => setNumQ(Number(e.target.value))}
            />
          </div>
          <div>
            <Label>{lang === "es" ? "Categoría" : "Category"}</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>{lang === "es" ? "Tópico" : "Topic"}</Label>
            <Input value={topic} onChange={(e) => setTopic(e.target.value)} />
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            data-testid="ai-generate-btn"
            onClick={generate}
            disabled={loading}
            className="brutal-btn bg-canales-red text-white px-4 py-2 flex items-center gap-2"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {loading
              ? lang === "es"
                ? "Generando…"
                : "Generating…"
              : lang === "es"
                ? "Generar preguntas"
                : "Generate questions"}
          </Button>
          {meta && (
            <div className="text-xs font-body self-center">
              {meta.count} · {Math.round(meta.source_chars / 1000)}k chars
            </div>
          )}
        </div>
      </div>

      {/* Preview + edit */}
      {questions.length > 0 && (
        <div className="brutal p-6 bg-canales-cream space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-display font-black text-2xl">
              {lang === "es" ? `Vista previa (${questions.length})` : `Preview (${questions.length})`}
            </h2>
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-2 font-body text-sm">
                <input
                  data-testid="ai-create-exam-toggle"
                  type="checkbox"
                  checked={createExam}
                  onChange={(e) => setCreateExam(e.target.checked)}
                />
                {lang === "es" ? "Crear examen" : "Create exam"}
              </label>
              <Button
                data-testid="ai-save-btn"
                onClick={save}
                disabled={saving}
                className="brutal-btn bg-canales-blue text-white px-4 py-2 flex items-center gap-2"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {lang === "es" ? "Guardar todo" : "Save all"}
              </Button>
            </div>
          </div>

          {createExam && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div><Label>Título ES</Label><Input data-testid="ai-exam-title-es" value={examTitleEs} onChange={(e) => setExamTitleEs(e.target.value)} /></div>
              <div><Label>Title EN</Label><Input data-testid="ai-exam-title-en" value={examTitleEn} onChange={(e) => setExamTitleEn(e.target.value)} /></div>
              <div><Label>{lang === "es" ? "Puntaje aprobación %" : "Passing %"}</Label><Input type="number" value={passingScore} onChange={(e) => setPassingScore(e.target.value)} /></div>
              <div><Label>{lang === "es" ? "Tiempo (seg)" : "Time (sec)"}</Label><Input type="number" value={timeLimit || ""} onChange={(e) => setTimeLimit(e.target.value)} /></div>
            </div>
          )}

          <div className="space-y-3">
            {questions.map((q, i) => (
              <div key={i} className="brutal-sm p-4 bg-white">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-xs font-display font-bold uppercase tracking-widest text-canales-red">
                    #{i + 1} · {q.topic}
                  </div>
                  <button onClick={() => removeQ(i)} className="text-red-600 text-xs flex items-center gap-1">
                    <Trash2 className="w-3 h-3" /> {lang === "es" ? "Quitar" : "Remove"}
                  </button>
                </div>
                <Textarea rows={2} value={q.prompt_es} onChange={(e) => patch(i, "prompt_es", e.target.value)} className="mt-1" />
                <Textarea rows={2} value={q.prompt_en} onChange={(e) => patch(i, "prompt_en", e.target.value)} className="mt-1" />
                <div className="grid sm:grid-cols-2 gap-2 mt-2">
                  {q.options.map((o, oi) => (
                    <div
                      key={oi}
                      className={`brutal-sm p-2 flex items-center gap-2 ${
                        q.correct === o.id ? "bg-green-50 border-green-700" : "bg-white"
                      }`}
                    >
                      <span className="font-display font-black">{o.id}.</span>
                      <Input value={o.es} onChange={(e) => patchOpt(i, oi, "es", e.target.value)} className="text-sm" />
                      <button
                        type="button"
                        onClick={() => patch(i, "correct", o.id)}
                        className={`px-2 py-1 text-xs rounded border-2 border-black font-display font-bold ${
                          q.correct === o.id ? "bg-green-600 text-white" : "bg-white"
                        }`}
                      >
                        {q.correct === o.id ? "✓" : "•"}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
}
